import { useState, useMemo, useRef, useEffect } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useMediaQuery } from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteButton from 'components/Button/DeleteButton';
import Avatar from 'components/Avatar';
import CalendarWidget from 'components/FormFields/CalendarWidget';
import InputField from 'components/FormFields/InputField';
import { ViewOptionsOption } from 'components/ViewOptions';
import { useTimeframe } from 'hooks/useTimeframe';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { useDetectClickOutside } from 'hooks/useDetectClickOutside';
import MoveIcon from 'assets/images/moveIcon.svg';
import { imagePickSize } from 'utils/general';
import MemberAssignment from '../MemberAssignment';
import TaskComments from '../TaskComments';
import TaskReminder from '../TaskReminder';
import './style.scss';

dayjs.extend(customParseFormat);

/** @typedef {import('../types.js').ChecklistVisibility} ChecklistVisibility */
/** @typedef {import('../types.js').DateString} DateString */
/** @typedef {import('../types.js').Timeframe} Timeframe */

/**
 * @param {object} props
 * @param {number} props.taskId
 * @param {string} props.title
 * @param {DateString} props.dueDate
 * @param {Timeframe} props.timeframe
 * @param {ChecklistVisibility} props.visibility
 * @param {string} props.details
 * @param {boolean} props.completed
 * @param {Array<object>} props.assignablePeople - List of logins
 * @param {Array<object>} props.assigned - List of logins
 * @param {string} props.assignmentNote - A note about assignments before assigned
 * @param {Array<object>} props.comments - List of Comments
 * @param {Array<object>} props.reminders - List of reminders
 * @param {boolean} props.highlighted
 * @param {number} props.currentLoginId
 * @param {string} props.timeZone
 * @param {string} props.dateFormat - Format string for displaying dates (e.g., 'MMM D, YYYY').
 * @param {string} props.timeFormat - Format string for displaying times (e.g., 'h:mm A').
 * @param {boolean} props.preopened - If the task should be opened on render.
 * @param {function(string): void} props.onChangeTitle
 * @param {function(Timeframe|DateString): void} props.onChangeDueDate - Timeframe or DateString
 * @param {function(ChecklistVisibility): void} props.onChangeVisibility
 * @param {function(string): void} props.onChangeDetails
 * @param {function(boolean): void} props.onCheck
 * @param {function(number): void} props.onDelete
 * @param {function(string): void} props.onChangeAssignmentNote
 * @param {function(number): void} props.onAssign
 * @param {function(number): void} props.onUnassign
 * @param {function(): void} props.getComments
 * @param {function(number): void} props.addComment
 * @param {function(number): void} props.editComment
 * @param {function(number): void} props.deleteComment
 * @param {function(number): void} props.onChangeHighlighted
 * @param {function(): void} props.onAddItem - adds a task above this task
 * @param {function(): void} props.getReminders
 * @param {function(object): void} props.createTaskReminder
 * @param {function(object): void} props.updateTaskReminder
 * @param {function(object): void} props.deleteTaskReminder
 * @param {boolean} [props.isSortable=false]
 * @param {object} props.dragHandleAttributes
 * @param {object} props.dragHandleListeners
 * @param {boolean} props.collapseOnOutsideClick
 */
const ChecklistItem = ({
  taskId,
  title = '',
  dueDate,
  timeframe,
  // visibility,
  details = '',
  completed = false,
  assignablePeople = [],
  assigned = [],
  assignmentNote = '',
  comments: unsortedComments = [],
  reminders = [],
  highlighted = false,
  currentLoginId,
  timeZone,
  dateFormat,
  timeFormat,
  preopened = false,
  collapseOnOutsideClick,

  onChangeTitle,
  onChangeDueDate,
  // onChangeVisibility,
  onChangeDetails,
  onCheck,
  onDelete,
  onChangeAssignmentNote,
  onAssign,
  onUnassign,
  getComments,
  addComment,
  editComment,
  deleteComment,
  onChangeHighlighted,
  onAddItem,
  getReminders,
  createTaskReminder,
  updateTaskReminder,
  deleteTaskReminder,
  isSortable = false,
  dragHandleAttributes,
  dragHandleListeners,
}) => {
  const [isOpen, setIsOpen] = useState(preopened);
  const [deadlineType, setDeadlineType] = useState(dueDate ? 'date' : 'timeframe'); // 'date' || 'timeframe'
  const [selectedDate, setSelectedDate] = useState(dueDate ? dayjs(dueDate).format(dateFormat) : '');
  const [timeframeError, setTimeframeError] = useState('');
  const timeframeRef = useRef();

  const isCondensed = useMediaQuery({ query: '(max-width: 1199px)' });

  const showPreAssignmentNote = !isOpen && assignmentNote && assigned.length === 0;

  const comments = useMemo(() => {
    const toSort = [...unsortedComments];
    return toSort.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)));
  }, [unsortedComments]);

  const processedReminders = reminders.map(r => ({
    definitionId: r.id,
    taskId: r.spec?.payload?.task_id || taskId,
    delta: r.spec?.delta || {},
  }));

  const ref = collapseOnOutsideClick ?
    useDetectClickOutside({ onTriggered: () => setIsOpen(false) }) :
    null;

  const { textareaRef } = useAutosizeTextarea();
  const { toString, fromString, ERROR_MSG } = useTimeframe();

  const formattedDate = useMemo(() => selectedDate ? dayjs(selectedDate).format(dateFormat) : null, [selectedDate]);
  const formattedTimeframe = useMemo(() => timeframe ? toString(timeframe) : null, [timeframe]);

  const pastDue = useMemo(() => {
    if (!dueDate) return false;
    if (!completed && dayjs(dueDate).isBefore(dayjs())) return true;
    return false;
  }, [dueDate, completed]);

  const hasDetails = details && details !== '';
  const hasComments = Array.isArray(comments) && comments.length > 0;

  const canBeHighlighted = !!onChangeHighlighted;

  useEffect(() => {
    if (textareaRef.current && !title || title === '') {
      textareaRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (preopened && ref.current) {
      const topPos = ref.current.getBoundingClientRect().top;
      window.scrollTo({
        top: topPos - 100,
        behavior: 'smooth',
      });
    }
  }, [ref]);

  const openTask = () => {
    if (isOpen) return;
    getComments();
    getReminders();
    setIsOpen(true);
  };

  const toggleTask = () => {
    if (isOpen) setIsOpen(false);
    else openTask();
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (deadlineType === 'date') {
      onChangeDueDate({
        type: 'date',
        value: date,
      });
    }
  };

  const handleTimeframeChange = (value) => {
    setTimeframeError('');

    if (deadlineType === 'timeframe') {
      const trimmed = value.trim();
      if (!trimmed) {
        onChangeDueDate({
          type: 'timeframe',
          value: null,
        });
        return;
      }

      const parsedTimeframe = fromString(trimmed);
      if (parsedTimeframe === undefined) {
        setTimeframeError(ERROR_MSG);
        if (timeframeRef.current) {
          timeframeRef.current.value = toString(timeframe) || '';
        }
        return;
      }

      onChangeDueDate({
        type: 'timeframe',
        value: parsedTimeframe,
      });
    }
  };

  // Wrapper functions to convert between GraphQL format (used by CalendarWidget)
  // and direct parameter format (used by LeadRecord handlers)
  const wrappedCreateTaskReminder = (params) => {
    const { taskId: reminderTaskId, input } = params.variables;
    createTaskReminder(reminderTaskId, input);
  };

  const wrappedUpdateTaskReminder = (params) => {
    const { taskId: reminderTaskId, definitionId, input } = params.variables;
    updateTaskReminder(reminderTaskId, definitionId, input);
  };

  const wrappedDeleteTaskReminder = (params) => {
    const { taskId: reminderTaskId, definitionId } = params.variables;
    deleteTaskReminder(reminderTaskId, definitionId);
  };

  return (
    <div className="task__wrapper">
      {onAddItem && (
        <button
          className="task__add-task-above add-task button--unstyled"
          onClick={onAddItem}
        >
          <span className="visually-hidden">Add additional task</span>
          <i className="add-task__icon">
            <FontAwesomeIcon icon={['fal', 'plus-circle']} />
          </i>
          <hr />
        </button>
      )}
      <div
        ref={ref}
        className={clsx('task', completed && 'completed', isOpen && 'opened', pastDue && 'past-due')}
      >

        <div
          className="task__head"
          onClick={toggleTask}
        >
          {isSortable && (
            <button
              className={clsx('task__move')}
              {...dragHandleAttributes}
              {...dragHandleListeners}
            >
              <img
                src={MoveIcon}
                alt="⋮⋮"
              />
            </button>
          )}
          <div
            className="task__checkmark"
            onClick={(e) => {
              e.stopPropagation();
              onCheck(!completed);
            }}
          />
          <div className="task__title">
            {hasComments ? (
              <FontAwesomeIcon
                icon={['fal', 'comment']}
                className="task-title__icon"
              />
            ) : hasDetails ? (
              <FontAwesomeIcon
                icon={['fal', 'file']}
                className="task-title__icon"
              />
            ) : null}
            {showPreAssignmentNote && (
              <span className="pre-assignment-note">[{assignmentNote}]</span>
            )}
            {isCondensed ? (
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => {
                  e.currentTarget.scrollLeft = 0;
                  onChangeTitle(e.currentTarget.textContent);
                }}
                onClick={(e) => {
                  if (isOpen) e.stopPropagation();
                }}
              >
                {title}
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                defaultValue={title}
                type="text"
                name="Task title"
                rows={1}
                onClick={(e) => {
                  // Prevent closing the item when clicking title textarea.
                  if (isOpen) e.stopPropagation();
                }}
                onBlur={(e) => onChangeTitle(e.target.value)}
              />
            )}

          </div>
          <div className="task__assignees">
            {assigned.map((assignee, idx) => {
              const found = assignablePeople.find(person => person.id === assignee.loginId);
              const imagePath = found && found.profileImage
                ? `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(found.profileImage, 'square60')}`
                : null;

              if (isCondensed && idx > 1) {
                return;
              }
              return (
                <Avatar
                  photo={imagePath}
                  text={!imagePath ? found?.initials || '' : null}
                  size={30}
                  borderSize={1}
                />
              );
            })}
            {(isCondensed && assigned.length > 2) && (
              <span className="task__more-assignees">
                +{assigned.length - 2}
              </span>
            )}
          </div>
          <div className="task__date-label">
            {formattedDate || formattedTimeframe}
          </div>
          {canBeHighlighted && (
            <button
              className={clsx('task__star', highlighted && 'highlighted')}
              onClick={onChangeHighlighted}
            >
              <FontAwesomeIcon icon={['fa', 'star']} />
            </button>
          )}
          <div className="task__delete">
            <DeleteButton onClick={onDelete} />
          </div>
        </div>
        <div className="task__body">
          <div className="task__details">
            <div className="task__body-heading">Details</div>
            <textarea
              defaultValue={details}
              onBlur={(e) => onChangeDetails(e.target.value)}
              placeholder="Add details"
            />
          </div>
          <div className="task__fields-group">
            <div className="task__deadline">
              <div className="task__body-heading">Deadline</div>
              <ViewOptionsOption
                id={`due-date-${taskId}`}
                inputProps={{
                  name: `deadline-type-${taskId}`,
                  className: 'task__radio-input',
                  checked: deadlineType === 'date',
                  onChange: () => { setTimeframeError(''); setDeadlineType('date'); },
                }}
              >
                Due Date
              </ViewOptionsOption>
              <ViewOptionsOption
                id={`timeframe-${taskId}`}
                inputProps={{
                  name: `deadline-type-${taskId}`,
                  className: 'task__radio-input',
                  checked: deadlineType === 'timeframe',
                  onChange: () => setDeadlineType('timeframe'),
                }}
              >
                Timeframe
              </ViewOptionsOption>

              {deadlineType === 'date' && (
                <>
                  <CalendarWidget
                    name="Due Date"
                    date={selectedDate}
                    pickerDate={selectedDate ? dayjs(selectedDate, dateFormat).format('MM/DD/YYYY') : ''}
                    dateFormatLabel={dateFormat}
                    setDate={handleDateChange}
                    showNextYears
                    hasClear
                    showLabel={false}
                    placeholder="Select due date..."
                    showReminder
                    shouldCloseOnSelect={false}
                    entityId={taskId}
                    reminders={processedReminders}
                    createReminder={wrappedCreateTaskReminder}
                    updateReminder={wrappedUpdateTaskReminder}
                    deleteReminder={wrappedDeleteTaskReminder}
                  />

                  {dueDate && (
                    <TaskReminder
                      reminders={processedReminders}
                      createReminder={createTaskReminder}
                      updateReminder={updateTaskReminder}
                      deleteReminder={deleteTaskReminder}
                      taskId={taskId}
                    />
                  )}
                </>
              )}

              {deadlineType === 'timeframe' && (
                <div>
                  <InputField
                    ref={timeframeRef}
                    name="timeframe"
                    showLabel={false}
                    defaultValue={toString(timeframe)}
                    onBlur={(e) => handleTimeframeChange(e.target.value)}
                    placeholder="Timeframe..."
                    className="task__timeframe-input"
                  />
                  {timeframeError && (
                    <div className="task__timeframe-error">
                      {timeframeError}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="task__assigned">
              <MemberAssignment
                taskId={taskId}
                members={assignablePeople}
                assignments={assigned}
                assignmentNote={assignmentNote}
                onChangeNote={onChangeAssignmentNote}
                onAdd={onAssign}
                onRemove={onUnassign}
              />
            </div>
          </div>
          <div className="task__comments">
            <TaskComments
              comments={comments}
              addComment={addComment}
              editComment={editComment}
              deleteComment={deleteComment}
              timeZone={timeZone}
              userId={currentLoginId}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistItem;
